import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Landmark, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";

const UnreadBadge = ({ count, testid }) =>
  count > 0 ? (
    <span
      data-testid={testid}
      className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
    >
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

export const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState({ userUnread: 0, adminUnread: 0 });

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const fetchUnread = () =>
      api
        .get("/tickets/unread")
        .then(({ data }) => active && setUnread({ userUnread: data.userUnread || 0, adminUnread: data.adminUnread || 0 }))
        .catch(() => {});
    fetchUnread();
    const timer = setInterval(fetchUnread, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLink = (to, label, testid, badge = null) => (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center text-sm transition-colors hover:text-foreground ${
        location.pathname === to ? "text-foreground font-medium" : "text-muted-foreground"
      }`}
    >
      {label}
      {badge}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="accent-topline" />
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 border border-primary/30">
            <Landmark className="h-4.5 w-4.5 h-5 w-5 text-primary" aria-hidden="true" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight">GetYourQSIP</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
          {navLink("/", "Home", "nav-home-link")}
          {navLink("/info", "How It Works", "nav-info-link")}
          {navLink("/membership", "Membership", "nav-membership-link")}
          {user &&
            navLink(
              "/support",
              "Support",
              "nav-support-link",
              <UnreadBadge count={unread.userUnread} testid="nav-support-unread-badge" />
            )}
        </nav>

        <div className="flex items-center gap-2.5">
          {user ? (
            <>
              {user.role === "admin" && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="nav-admin-button"
                  onClick={() => navigate("/admin")}
                >
                  <ShieldCheck className="h-4 w-4 mr-1.5" /> Admin
                  <UnreadBadge count={unread.adminUnread} testid="nav-admin-unread-badge" />
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                data-testid="nav-dashboard-button"
                onClick={() => navigate("/dashboard")}
              >
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
              </Button>
              <Button variant="ghost" size="sm" data-testid="nav-logout-button" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-1.5" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" data-testid="nav-login-button" onClick={() => navigate("/auth")}>
                Sign in
              </Button>
              <Button size="sm" data-testid="nav-get-membership-button" onClick={() => navigate("/membership")}>
                Get Membership
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
